// client/pages/Convert.tsx - Complete file with dark black desktop theme

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { ArrowDownUp, RefreshCw, TrendingUp, TrendingDown, Search, X, ArrowRightLeft, Loader2, ChevronDown, ArrowLeft, CheckCircle, AlertTriangle, Info, Copy, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMultipleTokenPrices } from "@/hooks/use-dexscreener";
import { useSolanaBalances } from "@/hooks/use-solana-balances";

// ============ HELPER FUNCTIONS ============
const extractHashFromMessage = (message: string): string => {
  const match = message.match(/([a-zA-Z0-9]{44,})/);
  return match ? match[1] : message;
};

// ============ TOKEN CONFIGURATION - SOLANA ONLY WITH SOL AS BASE ============
const TOKENS = [
  // Native SOL
  { id: "sol_solana", symbol: "SOL", name: "SOLANA", chain: "solana", address: "So11111111111111111111111111111111111111112", isNative: true },
  
  // Major Tokens on Solana
  { id: "usdt_solana", symbol: "USDT", name: "TETHER", chain: "solana", address: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB", isStable: true },
  { id: "usdc_solana", symbol: "USDC", name: "USD COIN", chain: "solana", address: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v", isStable: true },
  { id: "wbtc_solana", symbol: "WBTC", name: "WRAPPED BITCOIN", chain: "solana", address: "3NZ9JMVBmGAqocybic2c7LQCJScmgsAZ6vQqTDzcqmJh" },
  { id: "weth_solana", symbol: "WETH", name: "WRAPPED ETHEREUM", chain: "solana", address: "7vfCXTUXx5WJV5JADk17DUJ4ksgau7utNKj4b963voxs" },
  { id: "xrp_solana", symbol: "XRP", name: "RIPPLE", chain: "solana", address: "6UpQcMAb5xMzxc7ZfPaVMgx3KqsvKZdT5U718BzD5We2" },
  { id: "doge_solana", symbol: "DOGE", name: "DOGECOIN", chain: "solana", address: "B9u8h65uM1oifqmP82VyUDb68iG2fKfZiyubmNMtu3h7" },
  
  // Popular Solana Tokens
  { id: "pump_solana", symbol: "PUMP", name: "PUMP", chain: "solana", address: "pumpCmXqMfrsAkQ5r49WcJnRayYRqmXz6ae8H7H9Dfn" },
  { id: "orca_solana", symbol: "ORCA", name: "ORCA", chain: "solana", address: "orcaEKTdK7LKz57vaAYr9QeNsVEPfiu6QeMU1kektZE" },
  { id: "bonk_solana", symbol: "BONK", name: "BONK", chain: "solana", address: "DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263" },
  { id: "ray_solana", symbol: "RAY", name: "RAYDIUM", chain: "solana", address: "4k3Dyjzvzp8eMZWUXbBCjEvwSkkk59S5iCNLY3QrkX6R" },
];

// Tokens to show in marquee and left column
const DISPLAY_TOKENS_LIST = ["SOL", "USDT", "USDC", "WBTC", "WETH", "XRP", "DOGE", "BONK", "ORCA", "RAY", "PUMP"];

const TOKEN_ADDRESSES: string[] = [];
TOKENS.forEach(t => TOKEN_ADDRESSES.push(t.address));

const TOKEN_DECIMALS: Record<string, number> = {
  SOL: 9,
  USDT: 6,
  USDC: 6,
  WBTC: 6,
  WETH: 6,
  XRP: 6,
  DOGE: 6,
  BONK: 5,
  RAY: 6,
  ORCA: 6,
  PUMP: 6,
};

const CHAIN_NATIVE_TOKENS: Record<string, string> = {
  solana: "SOL",
};

const DEFAULT_SLIPPAGE_PERCENT = 0.5;
const DEFAULT_SLIPPAGE_BPS = DEFAULT_SLIPPAGE_PERCENT * 100;

const getDecimals = (token: string, chain?: string): number => {
  return TOKEN_DECIMALS[token] || 18;
};

// Check if token is a stablecoin (USDT or USDC)
const isStablecoin = (symbol: string): boolean => {
  return symbol === "USDT" || symbol === "USDC";
};

// ============ MARQUEE COMPONENTS ============
const DesktopMarquee = ({ prices }: { prices: Record<string, { priceUsd: number; priceChange24h: number }> }) => {
  const marqueeItems = DISPLAY_TOKENS_LIST.map(symbol => {
    const token = TOKENS.find(t => t.symbol === symbol);
    if (!token) return null;
    
    // Use constant price for stablecoins
    let priceData;
    if (isStablecoin(symbol)) {
      priceData = { priceUsd: 1.000, priceChange24h: 0 };
    } else {
      priceData = prices[token.address];
    }
    
    return {
      symbol,
      price: priceData?.priceUsd || 0,
      change: priceData?.priceChange24h || 0,
    };
  }).filter(item => item !== null && item.price > 0 && item.price < 1000000);
  
  const loopItems = [...marqueeItems, ...marqueeItems];

  const formatPrice = (symbol: string, price: number) => {
    if (!price) return "$0.00";
    if (symbol === "SOL" || symbol === "WBTC" || symbol === "WETH") {
      return `$${price.toFixed(2)}`;
    }
    if (isStablecoin(symbol)) {
      return `$${price.toFixed(3)}`;
    }
    return `$${price.toFixed(6)}`;
  };

  if (marqueeItems.length === 0) return null;

  return (
    <div className="overflow-hidden whitespace-nowrap border-b border-border py-2">
      <div className="inline-block animate-marquee-desktop">
        {loopItems.map((item, idx) => (
          <div key={idx} className="inline-flex items-center mx-4 text-[10px] gap-2">
            <span className="text-muted-foreground uppercase">{item.symbol}</span>
            <span className="text-foreground font-semibold">{formatPrice(item.symbol, item.price)}</span>
            <span className={`font-semibold ${item.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee-desktop { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee-desktop { animation: marquee-desktop 30s linear infinite; }
      `}</style>
    </div>
  );
};

const MobileMarquee = ({ prices }: { prices: Record<string, { priceUsd: number; priceChange24h: number }> }) => {
  const marqueeItems = DISPLAY_TOKENS_LIST.map(symbol => {
    const token = TOKENS.find(t => t.symbol === symbol);
    if (!token) return null;
    
    // Use constant price for stablecoins
    let priceData;
    if (isStablecoin(symbol)) {
      priceData = { priceUsd: 1.000, priceChange24h: 0 };
    } else {
      priceData = prices[token.address];
    }
    
    return {
      symbol,
      price: priceData?.priceUsd || 0,
      change: priceData?.priceChange24h || 0,
    };
  }).filter(item => item !== null && item.price > 0 && item.price < 1000000);
  
  const loopItems = [...marqueeItems, ...marqueeItems];

  const formatPrice = (symbol: string, price: number) => {
    if (!price) return "$0.00";
    if (symbol === "SOL" || symbol === "WBTC" || symbol === "WETH") {
      return `$${price.toFixed(2)}`;
    }
    if (isStablecoin(symbol)) {
      return `$${price.toFixed(3)}`;
    }
    return `$${price.toFixed(6)}`;
  };

  if (marqueeItems.length === 0) return null;

  return (
    <div className="overflow-hidden whitespace-nowrap flex-1">
      <div className="inline-block animate-marquee-mobile">
        {loopItems.map((item, idx) => (
          <div key={idx} className="inline-flex items-center mx-3 text-[10px] gap-1">
            <span className="text-muted-foreground uppercase">{item.symbol}</span>
            <span className="text-foreground font-semibold">{formatPrice(item.symbol, item.price)}</span>
            <span className={`font-semibold ${item.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
            </span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes marquee-mobile { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee-mobile { animation: marquee-mobile 40s linear infinite; }
      `}</style>
    </div>
  );
};

// ============ BIRDEYE CHART COMPONENT ============
const BirdeyeChart = ({ tokenAddress, chain }: { tokenAddress: string; chain: string }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (!tokenAddress || !chartRef.current) return;

    setIsLoading(true);

    const isDark = isDarkMode;
    const theme = isDark ? 'dark' : 'light';
    const backgroundColor = isDark ? '%23000000' : '%23FFFFFF';
    const textColor = isDark ? '%23FFFFFF' : '%23111A28';
    const borderColor = isDark ? '%233F3F46' : '%23E5E7EB';
    const upColor = '%2322C55E';
    const downColor = '%23EF4444';
    
    const widgetUrl = `https://embed.birdeye.so/tv-widget/${tokenAddress}?chain=${chain}&viewMode=pair&chartInterval=1D&chartType=Candle&chartTimezone=Asia%2FKarachi&chartLeftToolbar=hide&theme=${theme}&backgroundColor=${backgroundColor}&textColor=${textColor}&borderColor=${borderColor}&chartOverrides=mainSeriesProperties.candleStyle.downColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.borderDownColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.wickDownColor=${downColor}&chartOverrides=mainSeriesProperties.candleStyle.upColor=${upColor}&chartOverrides=mainSeriesProperties.candleStyle.borderUpColor=${upColor}&chartOverrides=mainSeriesProperties.candleStyle.wickUpColor=${upColor}`;

    const iframe = document.createElement('iframe');
    iframe.src = widgetUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '12px';
    iframe.style.background = 'transparent';
    iframe.allow = 'clipboard-write';
    iframe.onload = () => {
      setIsLoading(false);
    };

    chartRef.current.innerHTML = '';
    chartRef.current.appendChild(iframe);

    return () => {
      if (chartRef.current) {
        chartRef.current.innerHTML = '';
      }
    };
  }, [tokenAddress, chain, isDarkMode]);

  return (
    <div className="bg-card border border-border rounded-xl p-4 h-full flex flex-col shadow-sm">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="text-[12px] font-bold uppercase tracking-wider text-foreground">
            {TOKENS.find(t => t.address === tokenAddress)?.symbol || 'Token'} / SOL
          </h3>
          <p className="text-[8px] text-muted-foreground uppercase">Birdeye Chart</p>
        </div>
        <div className="flex gap-1">
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">1H</button>
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">24H</button>
          <button className="text-[8px] text-yellow-500 uppercase px-2 py-0.5 border border-yellow-500 rounded">7D</button>
        </div>
      </div>
      
      <div className="flex-1 relative min-h-[300px]">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg">
            <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
          </div>
        )}
        <div ref={chartRef} className="w-full h-full rounded-lg overflow-hidden" />
      </div>
    </div>
  );
};

// ============ TOKEN PRICE LIST (LEFT COLUMN) - NO SCROLL BAR, TRANSPARENT SELECTION ============
const TokenPriceList = ({ prices, onSelectToken, selectedToken }: any) => {
  const formatPrice = (symbol: string, price: number) => {
    if (!price || price === 0 || price > 1000000) return "$0.00";
    if (symbol === "SOL" || symbol === "WBTC" || symbol === "WETH") {
      return `$${price.toFixed(2)}`;
    }
    if (isStablecoin(symbol)) {
      return `$${price.toFixed(3)}`;
    }
    return `$${price.toFixed(6)}`;
  };

  const formatChange = (change: number) => {
    if (!change || isNaN(change)) return "0.00%";
    return `${change >= 0 ? '+' : ''}${change.toFixed(2)}%`;
  };

  const displayTokens = TOKENS.filter(t => DISPLAY_TOKENS_LIST.includes(t.symbol));

  return (
    <div className="bg-transparent border border-border rounded-xl p-4 h-full flex flex-col overflow-hidden">
      <div className="border-b border-border pb-2 mb-2 flex-shrink-0">
        <div className="flex justify-between items-center">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-yellow-500">Token Prices</h3>
          <button className="text-[8px] text-muted-foreground hover:text-foreground uppercase px-2 py-0.5 border border-border rounded">
            24H Change
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="space-y-1">
          {displayTokens.map((token) => {
            // Use constant price for stablecoins
            let priceData;
            if (isStablecoin(token.symbol)) {
              priceData = { priceUsd: 1.000, priceChange24h: 0 };
            } else {
              priceData = prices[token.address];
            }
            
            let price = priceData?.priceUsd || 0;
            const change = priceData?.priceChange24h || 0;
            const isSelected = selectedToken === token.symbol;

            if (price > 1000000 || price < 0) price = 0;

            return (
              <button
                key={token.id}
                onClick={() => onSelectToken(token)}
                className={cn(
                  "w-full flex items-center justify-between px-2 py-2 rounded-lg transition-colors",
                  isSelected ? "bg-transparent" : "hover:bg-muted/50"
                )}
              >
                <span className="text-[11px] font-bold text-foreground uppercase">{token.symbol}</span>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-foreground font-mono">{formatPrice(token.symbol, price)}</span>
                  <span className={cn(
                    "text-[10px] font-medium w-16 text-right",
                    change >= 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {formatChange(change)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============ TOKEN DROPDOWN - FIXED ============
const TokenDropdown = React.forwardRef(({ value, onChange, isOpen, setIsOpen, getTokenBalance }: any, ref) => {
  // Find the selected token object
  const selectedTokenObj = TOKENS.find(t => t.symbol === value);
  
  return (
    <div className="relative" ref={ref}>
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="bg-card border border-border rounded-lg px-3 py-2 text-[10px] font-medium uppercase flex items-center gap-1 min-w-[90px] justify-between hover:bg-muted transition h-9 text-foreground"
      >
        <span>{selectedTokenObj?.symbol || value}</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-card border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {TOKENS.map((token) => {
            const balance = getTokenBalance ? getTokenBalance(token.symbol) : 0;
            const isNative = token.isNative;
            const isStable = token.isStable;
            return (
              <button 
                key={token.id} 
                onClick={() => { 
                  onChange(token.symbol); 
                  setIsOpen(false); 
                }} 
                className="w-full px-3 py-2 text-left hover:bg-muted transition flex items-center justify-between text-[10px]"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium uppercase text-foreground">{token.symbol}</span>
                  {isNative && <span className="text-[6px] text-yellow-500 uppercase">★</span>}
                  {isStable && <span className="text-[6px] text-blue-500 uppercase">STABLE</span>}
                  <span className="text-[6px] text-muted-foreground uppercase">{token.chain}</span>
                </div>
                <span className="text-[10px] font-mono text-muted-foreground">{balance.toFixed(4)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
TokenDropdown.displayName = "TokenDropdown";

// ============ SWAP CARD - SAME AS SWAP.TSX ============
const SwapCard = ({
  tradeType, setTradeType,
  selectedToken, setSelectedToken,
  amount, setAmount,
  solAmount, setSolAmount,
  quote, quoteLoading, swapLoading,
  currentPrice,
  getTokenBalance,
  handleAmountChange, handleSolAmountChange,
  handleMax, handleSwap,
  showTokenDropdown, setShowTokenDropdown,
  tokenDropdownRef,
  TokenDropdown,
  minimumAmount,
  isDesktop,
  navigate,
}: any) => {
  const cardClassName = isDesktop 
    ? "bg-card border border-border rounded-xl p-6 h-full shadow-sm"
    : "bg-transparent p-0";

  // Always use SOL as the native token
  const nativeSymbol = "SOL";
  const nativeBalance = getTokenBalance(nativeSymbol) || 0;

  return (
    <div className={cardClassName}>
      {/* Header - Clickable text to navigate to /limit-order */}
      <div className="mb-4">
        <button
          onClick={() => navigate("/limit-order")}
          className="text-[11px] font-bold uppercase tracking-wider text-yellow-500 hover:text-yellow-400 transition-colors cursor-pointer"
        >
          Advanced Trade Execution →
        </button>
      </div>

      {/* BUY/SELL buttons - Simple buttons without toggle card */}
      <div className="flex gap-2 mb-4">
        <button 
          onClick={() => setTradeType("buy")} 
          className={cn(
            "flex-1 py-2 rounded-lg text-[10px] font-medium uppercase transition border",
            tradeType === "buy" 
              ? "bg-yellow-500 text-white border-yellow-500" 
              : "bg-transparent text-muted-foreground border-border hover:bg-muted"
          )}
        >
          BUY
        </button>
        <button 
          onClick={() => setTradeType("sell")} 
          className={cn(
            "flex-1 py-2 rounded-lg text-[10px] font-medium uppercase transition border",
            tradeType === "sell" 
              ? "bg-red-500 text-white border-red-500" 
              : "bg-transparent text-muted-foreground border-border hover:bg-muted"
          )}
        >
          SELL
        </button>
      </div>

      <div className="space-y-2 mb-4">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block">{tradeType === "buy" ? "BUY" : "SELL"} TOKEN</label>
        <div className="flex gap-2">
          <TokenDropdown 
            value={selectedToken} 
            onChange={setSelectedToken} 
            isOpen={showTokenDropdown} 
            setIsOpen={setShowTokenDropdown} 
            ref={tokenDropdownRef} 
            getTokenBalance={getTokenBalance} 
          />
          <Input 
            type="number" 
            placeholder="0.00" 
            value={amount} 
            onChange={(e) => handleAmountChange(e.target.value)} 
            className="flex-1 text-right text-[10px] bg-card border border-border text-foreground h-9 focus:outline-none focus:border-yellow-500" 
            disabled={quoteLoading || swapLoading} 
          />
        </div>
        <p className="text-[10px] text-muted-foreground text-right uppercase">
          Balance: {getTokenBalance(selectedToken).toFixed(6)} {selectedToken}
        </p>
      </div>

      <div className="space-y-2 mb-4">
        <label className="text-[10px] uppercase tracking-wider text-muted-foreground block">SOL AMOUNT</label>
        <Input 
          type="number" 
          placeholder="0.00" 
          value={solAmount} 
          onChange={(e) => handleSolAmountChange(e.target.value)} 
          className="w-full text-right text-[10px] bg-card border border-border text-foreground h-9 focus:outline-none focus:border-yellow-500" 
          disabled={quoteLoading || swapLoading} 
        />
        <p className="text-[10px] text-muted-foreground text-right uppercase">
          Available: {nativeBalance.toFixed(6)} SOL
        </p>
      </div>

      {quoteLoading ? (
        <div className="bg-muted p-3 rounded-lg text-center mb-4">
          <Loader2 className="w-3 h-3 animate-spin inline mr-1 text-yellow-500" />
          <span className="text-[8px] text-muted-foreground">Fetching quote...</span>
        </div>
      ) : quote ? (
        <div className="bg-muted p-3 rounded-lg space-y-1 mb-4 border border-border">
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground uppercase">YOU {tradeType === "buy" ? "PAY" : "RECEIVE"}</span>
            <span className="font-medium text-foreground">{quote.inputAmount.toFixed(6)} {tradeType === "buy" ? "SOL" : selectedToken}</span>
          </div>
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground uppercase">YOU {tradeType === "buy" ? "RECEIVE" : "PAY"}</span>
            <span className="font-medium text-foreground">{quote.outputAmount.toFixed(6)} {tradeType === "buy" ? selectedToken : "SOL"}</span>
          </div>
          <div className="flex justify-between text-[10px]">
            <span className="text-muted-foreground uppercase">PRICE</span>
            <span className="font-medium text-foreground">1 {selectedToken} = {currentPrice.toFixed(6)} SOL</span>
          </div>
          {quote.priceImpact !== 0 && (
            <div className="flex justify-between text-[8px]">
              <span className="text-muted-foreground uppercase">PRICE IMPACT</span>
              <span className={cn("font-medium", quote.priceImpact > 2 ? "text-orange-400" : "text-foreground")}>
                {quote.priceImpact.toFixed(2)}%
              </span>
            </div>
          )}
          <div className="flex justify-between text-[8px] border-t border-border pt-1 mt-1">
            <span className="text-muted-foreground uppercase">SLIPPAGE</span>
            <span className={cn(
              "font-medium",
              quote.slippageUsed && quote.slippageUsed > 5 ? "text-orange-400" : 
              quote.slippageUsed && quote.slippageUsed > 2 ? "text-yellow-500" : 
              "text-muted-foreground"
            )}>
              {quote.slippageUsed?.toFixed(1)}%
              {quote.slippageUsed && quote.slippageUsed > DEFAULT_SLIPPAGE_PERCENT && (
                <span className="ml-1 text-yellow-500 text-[8px]">(auto-adjusted)</span>
              )}
            </span>
          </div>
        </div>
      ) : null}

      <Button 
        onClick={handleSwap} 
        disabled={!amount || !solAmount || swapLoading || parseFloat(amount) < 0.000001} 
        className={cn("w-full h-10 text-[10px] font-bold uppercase", tradeType === "buy" ? "bg-yellow-500 text-white hover:bg-yellow-600" : "bg-red-500 text-white hover:bg-red-600")}
      >
        {swapLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> PROCESSING...</> : tradeType === "buy" ? `BUY ${selectedToken}` : `SELL ${selectedToken}`}
      </Button>
    </div>
  );
};

// ============ SIMPLE NOTIFICATION ============
const SimpleNotification = ({ message, type, onClose }: { message: string; type: "success" | "error" | "info"; onClose: () => void }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);
  return (
    <div className={cn("fixed top-20 right-4 z-[200] p-3 rounded-lg shadow-lg border animate-slide-in", type === "success" && "bg-green-500/90 border-green-400 text-white", type === "error" && "bg-red-500/90 border-red-400 text-white", type === "info" && "bg-blue-500/90 border-blue-400 text-white")}>
      <div className="flex items-center gap-2">
        {type === "success" && <CheckCircle className="w-4 h-4" />}
        {type === "error" && <AlertTriangle className="w-4 h-4" />}
        {type === "info" && <Info className="w-4 h-4" />}
        <p className="text-[10px] font-medium uppercase">{message}</p>
        <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3 h-3" /></button>
      </div>
    </div>
  );
};

// ============ SWAP SUCCESS DIALOG ============
const SwapSuccessDialog = ({ transactionHash, onClose, tradeType }: { transactionHash: string; onClose: () => void; tradeType: "buy" | "sell" }) => {
  const [copied, setCopied] = useState(false);

  const shortHash = transactionHash ? `${transactionHash.slice(0, 8)}...${transactionHash.slice(-8)}` : '';

  const handleCopyHash = () => {
    navigator.clipboard.writeText(transactionHash);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleViewOnExplorer = () => {
    window.open(`https://solscan.io/tx/${transactionHash}`, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-green-500/50 rounded-xl p-6 w-full max-w-sm space-y-4 shadow-lg">
        <div className="flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-6 h-6 text-green-500" />
          </div>
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-lg font-bold uppercase text-green-500">
            {tradeType === "buy" ? "BUY" : "SELL"} SUCCESSFUL
          </h2>
          <p className="text-[10px] text-muted-foreground uppercase">Transaction completed</p>
        </div>

        <div className="bg-muted rounded-lg p-4 space-y-2 border border-border">
          <p className="text-[10px] text-muted-foreground uppercase">Transaction Hash</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[10px] font-mono text-yellow-500 break-all">{shortHash}</code>
            <button
              onClick={handleCopyHash}
              className={cn(
                "p-2 rounded transition-colors",
                copied ? "bg-green-500/20 text-green-400" : "bg-muted hover:bg-muted/80 text-muted-foreground"
              )}
              title={copied ? "Copied!" : "Copy"}
            >
              <Copy className="w-3 h-3" />
            </button>
          </div>
          <p className="text-[8px] text-muted-foreground">Full: {transactionHash}</p>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleViewOnExplorer}
            className="flex-1 bg-yellow-500 text-white hover:bg-yellow-600 h-9 text-[10px] font-bold uppercase flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-3 h-3" />
            View Explorer
          </Button>
          <Button
            onClick={onClose}
            className="flex-1 bg-green-500 text-white hover:bg-green-600 h-9 text-[10px] font-bold uppercase"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============ BACKUP FILE DIALOG ============
const BackupFileDialog = ({ show, onClose, onSubmit, error, loading = false }) => {
  const [manualBackupText, setManualBackupText] = useState("");
  const [manualError, setManualError] = useState("");

  if (!show) return null;

  const validateBackupContent = (content: string): boolean => {
    if (content.length > 1024 * 1024) throw new Error("File too large. Maximum size is 1MB.");
    const parsed = JSON.parse(content);
    if (!parsed.magic || !parsed.salt || !parsed.iv || !parsed.data) throw new Error("Invalid backup file structure.");
    if (parsed.magic !== "FIXORIUM-MPC") throw new Error("Invalid backup file format.");
    return true;
  };

  const handleManualSubmit = () => {
    if (!manualBackupText.trim()) {
      setManualError("Please paste your backup JSON");
      return;
    }
    try {
      validateBackupContent(manualBackupText);
      onSubmit(manualBackupText);
      setManualBackupText("");
      setManualError("");
    } catch (err: any) {
      setManualError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 max-w-sm w-full mx-4 shadow-lg">
        {(error || manualError) && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">{error || manualError}</div>}
        
        <div className="space-y-3">
          <textarea
            value={manualBackupText}
            onChange={(e) => setManualBackupText(e.target.value)}
            placeholder='Paste your backup Key here...'
            className="w-full h-32 bg-muted border border-border rounded-lg p-2 text-[8px] font-mono text-foreground resize-none focus:outline-none focus:border-yellow-500"
          />
          <Button onClick={handleManualSubmit} disabled={loading} className="w-full bg-yellow-500 text-white hover:bg-yellow-600 text-sm">
            Verify & Use
          </Button>
        </div>

        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="flex-1 border-border text-foreground hover:bg-muted" disabled={loading}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

// ============ PASSWORD DIALOG ============
const PasswordDialog = ({ show, onClose, onSubmit, error, loading }: any) => {
  const [password, setPassword] = useState("");
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-sm w-full p-5 shadow-xl">
        <h3 className="text-[10px] font-bold uppercase mb-3 text-yellow-500">ENTER PASSWORD</h3>
        <p className="text-[8px] text-muted-foreground mb-4 uppercase">PASSWORD REQUIRED TO COMPLETE SWAP</p>
        {error && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-[8px] text-red-600 uppercase">{error}</div>}
        <input 
          type="password" 
          value={password} 
          onChange={(e) => setPassword(e.target.value)} 
          placeholder="ENTER PASSWORD" 
          className="w-full bg-muted border border-border rounded-md px-3 py-2 text-[10px] text-foreground uppercase mb-4 focus:outline-none focus:border-yellow-500"
          onKeyPress={(e) => e.key === "Enter" && onSubmit(password)} 
          autoFocus 
          disabled={loading}
        />
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 text-[10px] uppercase border-border text-foreground hover:bg-muted h-9" disabled={loading}>CANCEL</Button>
          <Button onClick={() => onSubmit(password)} disabled={!password.trim() || loading} className="flex-1 bg-yellow-500 text-white hover:bg-yellow-600 text-[10px] uppercase font-bold h-9">{loading ? "PROCESSING..." : "CONFIRM"}</Button>
        </div>
      </div>
    </div>
  );
};

// ============ CONFIRMATION DIALOG ============
const ConfirmationDialog = ({ show, onClose, onConfirm, details, loading }: any) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-sm w-full p-5 shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-[10px] font-bold uppercase tracking-wider text-yellow-500">CONFIRM SWAP</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="space-y-2">
          <div className="flex justify-between items-center py-1">
            <span className="text-[10px] text-muted-foreground uppercase">YOU PAY</span>
            <span className="text-[10px] text-foreground font-mono uppercase">{details?.payAmount} {details?.payToken}</span>
          </div>
          <div className="flex justify-between items-center py-1">
            <span className="text-[10px] text-muted-foreground uppercase">YOU RECEIVE</span>
            <span className="text-[10px] text-green-600 font-mono uppercase">{details?.receiveAmount} {details?.receiveToken}</span>
          </div>
          <div className="border-t border-border my-2"></div>
          <div className="flex justify-between items-center py-1">
            <span className="text-[10px] font-bold text-muted-foreground uppercase">TOTAL DEDUCTED</span>
            <span className="text-[10px] font-bold text-red-600 uppercase">{details?.payAmount} {details?.payToken}</span>
          </div>
        </div>
        
        <div className="flex gap-2 mt-5 pt-2">
          <Button onClick={onClose} variant="outline" className="flex-1 text-[10px] uppercase border-border text-foreground hover:bg-muted h-9">CANCEL</Button>
          <Button onClick={onConfirm} disabled={loading} className="flex-1 bg-yellow-500 text-white hover:bg-yellow-600 text-[10px] uppercase font-bold h-9">
            {loading ? "PROCESSING..." : "CONFIRM"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN COMPONENT ============
export default function Convert() {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // Swap state - same as Swap.tsx
  const [tradeType, setTradeType] = useState<"buy" | "sell">("buy");
  const [selectedToken, setSelectedToken] = useState<string>("SOL");
  const [amount, setAmount] = useState<string>("");
  const [solAmount, setSolAmount] = useState<string>("");
  const [quote, setQuote] = useState<any>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [swapLoading, setSwapLoading] = useState(false);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const tokenDropdownRef = useRef<HTMLDivElement>(null);

  const [tokenBalances, setTokenBalances] = useState<any[]>([]);
  const [isMPCUser, setIsMPCUser] = useState(false);
  const [backupFileContent, setBackupFileContent] = useState<string | null>(null);
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [pendingSwap, setPendingSwap] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState({ open: false, password: "", showPassword: false });
  const [notification, setNotification] = useState<{ message: string; type: "success" | "error" | "info" } | null>(null);
  const [processingSwap, setProcessingSwap] = useState(false);
  const [swapSuccessDialog, setSwapSuccessDialog] = useState<{ show: boolean; transactionHash: string } | null>(null);
  const processingTimerRef = useRef<NodeJS.Timeout | null>(null);

  const tokenAddressesList = useMemo(() => TOKENS.map(token => token.address), []);
  const { prices: dexPrices, loading: pricesLoading } = useMultipleTokenPrices(tokenAddressesList, { refetchInterval: 30000 });

  const displayPrices = useMemo(() => {
    const result: Record<string, { priceUsd: number; priceChange24h: number }> = {};
    TOKENS.forEach(token => {
      // Use constant price for stablecoins
      if (isStablecoin(token.symbol)) {
        result[token.address] = {
          priceUsd: 1.000,
          priceChange24h: 0,
        };
        return;
      }
      
      const priceData = dexPrices[token.address];
      let price = priceData?.priceUsd || 0;
      if (price > 1000000 || price < 0) price = 0;
      result[token.address] = {
        priceUsd: price,
        priceChange24h: priceData?.priceChange24h || 0,
      };
    });
    return result;
  }, [dexPrices]);

  const getTokenBySymbol = (symbol: string) => {
    return TOKENS.find(t => t.symbol === symbol);
  };

  // Get SOL price in USD
  const solPrice = displayPrices[TOKENS.find(t => t.symbol === "SOL")?.address || ""]?.priceUsd || 0;

  // Get token price in SOL
  const tokenPrices = useMemo(() => {
    const mappedPrices: Record<string, number> = {};
    TOKENS.forEach(token => {
      if (token.symbol === "SOL") {
        mappedPrices[token.symbol] = 1;
        return;
      }
      
      // For stablecoins, price in SOL = 1 / SOL price
      if (isStablecoin(token.symbol)) {
        mappedPrices[token.symbol] = solPrice > 0 ? 1 / solPrice : 0;
        return;
      }
      
      const address = token.address;
      const dexPrice = dexPrices[address]?.priceUsd;
      if (dexPrice && dexPrice > 0 && !isNaN(dexPrice) && solPrice > 0) {
        mappedPrices[token.symbol] = dexPrice / solPrice;
      } else {
        mappedPrices[token.symbol] = 0;
      }
    });
    return mappedPrices;
  }, [dexPrices, solPrice]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch("/api/auth", { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include" });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            setUser(data.user);
            setIsMPCUser(data.user.isMPC === true);
            setLoading(false);
            return;
          }
        }
        navigate("/login");
      } catch (error) {
        console.error("[Convert] Auth error:", error);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  const solanaWalletAddress = user?.wallets?.solana || null;
  const { balances: solanaBalances, refetch: refetchBalances } = useSolanaBalances({
    walletAddress: solanaWalletAddress || "",
    refetchInterval: false,
    enabled: !!solanaWalletAddress,
  });

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadBalances = async () => {
      if (!solanaWalletAddress) return;
      
      const balances: any[] = [];
      for (const token of TOKENS) {
        const balance = solanaBalances?.[token.symbol] || 0;
        balances.push({ symbol: token.symbol, balance, chain: token.chain });
      }
      if (balances.length > 0) setTokenBalances(balances);
    };
    loadBalances();
  }, [solanaBalances, solanaWalletAddress]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tokenDropdownRef.current && !tokenDropdownRef.current.contains(event.target as Node)) setShowTokenDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
    };
  }, []);

  const getTokenBalance = (symbol: string): number => {
    const token = tokenBalances.find(t => t.symbol === symbol);
    return token?.balance || 0;
  };
  
  const currentPrice = tokenPrices[selectedToken] || 0;
  const minimumAmount = 0.000001;

  const handleAmountChange = (value: string) => {
    setAmount(value);
    if (value && currentPrice > 0) {
      const calculatedSol = parseFloat(value) * currentPrice;
      setSolAmount(calculatedSol.toFixed(6));
    } else {
      setSolAmount("");
    }
  };

  const handleSolAmountChange = (value: string) => {
    setSolAmount(value);
    if (value && currentPrice > 0) {
      const calculatedAmount = parseFloat(value) / currentPrice;
      setAmount(calculatedAmount.toFixed(6));
    } else {
      setAmount("");
    }
  };

  useEffect(() => {
    const fetchQuote = async () => {
      if (!amount || parseFloat(amount) <= 0 || !selectedToken || currentPrice === 0) { setQuote(null); return; }
      if (selectedToken === "SOL") { setQuote(null); return; }
      if (tradeType === "sell" && parseFloat(amount) < minimumAmount) { setQuote(null); return; }
      if (tradeType === "buy" && parseFloat(solAmount) < 0.000001) { setQuote(null); return; }
      
      setQuoteLoading(true);
      try {
        const fromMint = tradeType === "buy" ? TOKENS.find(t => t.symbol === "SOL")?.address : TOKENS.find(t => t.symbol === selectedToken)?.address;
        const toMint = tradeType === "buy" ? TOKENS.find(t => t.symbol === selectedToken)?.address : TOKENS.find(t => t.symbol === "SOL")?.address;
        
        if (!fromMint || !toMint) { setQuote(null); return; }
        
        const decimals = tradeType === "buy" ? 9 : TOKEN_DECIMALS[selectedToken] || 9;
        const amountInSmallestUnits = Math.floor(parseFloat(tradeType === "buy" ? solAmount : amount) * Math.pow(10, decimals));
        
        if (amountInSmallestUnits < 1000) { setQuote(null); return; }
        
        const response = await fetch("/api/dflow", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ 
            action: "quote", 
            params: { 
              inputMint: fromMint, 
              outputMint: toMint, 
              amount: amountInSmallestUnits.toString(), 
              slippageBps: DEFAULT_SLIPPAGE_BPS.toString() 
            } 
          }),
        });
        if (!response.ok) throw new Error("Failed to get quote");
        const quoteData = await response.json();
        if (!quoteData.outAmount || parseFloat(quoteData.outAmount) === 0) throw new Error("No valid quote");
        
        const outputAmount = parseFloat(quoteData.outAmount) / Math.pow(10, decimals);
        const inputAmount = parseFloat(quoteData.inAmount) / Math.pow(10, 9);
        const slippageUsed = quoteData._slippageBps ? quoteData._slippageBps / 100 : DEFAULT_SLIPPAGE_PERCENT;
        
        setQuote({ 
          inputAmount, 
          outputAmount, 
          price: currentPrice, 
          priceImpact: parseFloat(quoteData.priceImpactPct) || 0,
          slippageUsed 
        });
      } catch (error: any) {
        console.error("[Convert] Quote error:", error);
        setQuote(null);
      } finally { setQuoteLoading(false); }
    };
    const debounceTimer = setTimeout(fetchQuote, 800);
    return () => clearTimeout(debounceTimer);
  }, [amount, solAmount, selectedToken, tradeType, currentPrice, minimumAmount]);

  const executeMarketSwap = async (password: string) => {
    if (!user || !amount || parseFloat(amount) <= 0) {
      setNotification({ message: "Please enter a valid amount", type: "error" });
      setSwapLoading(false);
      setProcessingSwap(false);
      return;
    }

    const fromToken = getTokenBySymbol(selectedToken);
    const toToken = getTokenBySymbol("SOL");

    if (!fromToken || !toToken) {
      setNotification({ message: "Token not found", type: "error" });
      setSwapLoading(false);
      setProcessingSwap(false);
      return;
    }

    // Single-chain swap using DFlow (all on Solana)
    const fromMint = tradeType === "buy" ? toToken.address : fromToken.address;
    const toMint = tradeType === "buy" ? fromToken.address : toToken.address;

    const decimals = tradeType === "buy" ? 9 : TOKEN_DECIMALS[selectedToken] || 9;
    const quantityInSmallestUnits = Math.floor(parseFloat(tradeType === "buy" ? solAmount : amount) * Math.pow(10, decimals));

    if (quantityInSmallestUnits < 1000) {
      setNotification({ message: "Amount too small", type: "error" });
      setSwapLoading(false);
      setProcessingSwap(false);
      return;
    }

    try {
      const quoteResponse = await fetch("/api/dflow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          action: "quote",
          params: {
            inputMint: fromMint,
            outputMint: toMint,
            amount: quantityInSmallestUnits.toString(),
            slippageBps: DEFAULT_SLIPPAGE_BPS.toString()
          }
        }),
      });

      if (!quoteResponse.ok) {
        const errorText = await quoteResponse.text();
        throw new Error(`Quote failed: ${quoteResponse.status} - ${errorText}`);
      }

      const quoteData = await quoteResponse.json();
      if (!quoteData.outAmount) {
        throw new Error("No quote received from DFlow");
      }

      const swapResponse = await fetch("/api/dflow", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          action: "swap",
          params: {
            quoteResponse: quoteData,
            userPublicKey: solanaWalletAddress
          }
        }),
      });

      if (!swapResponse.ok) {
        const errorText = await swapResponse.text();
        throw new Error(`Swap failed: ${swapResponse.status} - ${errorText}`);
      }

      const swapData = await swapResponse.json();
      if (!swapData.swapTransaction) {
        throw new Error("No swap transaction in response");
      }

      const swapPayload: any = {
        userId: user.email,
        password,
        type: "market_swap",
        fromMint,
        toMint,
        quantity: quantityInSmallestUnits,
        swapTransaction: swapData.swapTransaction,
        quoteResponse: quoteData,
        originalTokenAmount: parseFloat(amount),
      };

      if (isMPCUser && backupFileContent) {
        swapPayload.backupFile = backupFileContent;
      }

      const response = await fetch("/api/swap", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(swapPayload),
      });

      const result = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          if (result.needsBackup) throw new Error("MPC backup file required");
          throw new Error(result.error || "Invalid credentials");
        }
        throw new Error(result.error || "Swap failed");
      }

      if (response.status === 202 || result.transactionBroadcast) {
        setNotification({
          message: "Transaction broadcast. Check explorer for status.",
          type: "info",
        });
        return;
      }

      if (!result.success || !result.transactionHash) {
        throw new Error("Swap did not return a confirmed transaction");
      }

      setSwapSuccessDialog({ show: true, transactionHash: result.transactionHash });
      setAmount("");
      setSolAmount("");
      setQuote(null);
    } catch (error: any) {
      console.error("[Convert] Swap error:", error);
      throw error;
    }
  };

  const handleSwap = async (password?: string) => {
    if (!user) { setNotification({ message: "User not found", type: "error" }); return; }
    if (selectedToken === "SOL") { setNotification({ message: "Cannot trade SOL for itself", type: "error" }); return; }
    if (isMPCUser && !backupFileContent) { setPendingSwap(true); setShowBackupDialog(true); return; }
    if (!password) { setPasswordDialog({ ...passwordDialog, open: true }); return; }

    setPasswordDialog({ open: false, password: "", showPassword: false });
    setSwapLoading(true);
    setProcessingSwap(true);

    try {
      await executeMarketSwap(password);
    } catch (error: any) {
      console.error("[Convert] Swap error:", error);
      setNotification({ message: error.message || "Swap failed", type: "error" });
    } finally {
      setSwapLoading(false);
      setProcessingSwap(false);
    }
  };

  const handleMax = () => {
    const balance = getTokenBalance(selectedToken);
    setAmount(balance.toString());
    setSolAmount((balance * currentPrice).toFixed(6));
  };

  const handleBackupSubmit = (backupContent: string) => {
    setBackupFileContent(backupContent);
    setShowBackupDialog(false);
    setNotification({ message: "Backup file loaded successfully!", type: "success" });
    if (pendingSwap) { setPendingSwap(false); setTimeout(() => setPasswordDialog({ ...passwordDialog, open: true }), 100); }
  };

  const handleCancelBackup = () => {
    setShowBackupDialog(false);
    setPendingSwap(false);
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-yellow-500" /></div>;

  if (isDesktop) {
    return (
      <div className="desktop-dark min-h-screen bg-background text-foreground px-4 pb-4 pt-0">
        {/* Desktop-only dark black theme override — layout untouched */}
        <style>{`
          .desktop-dark {
            --background: 0 0% 0%;
            --card: 240 10% 4%;
            --popover: 240 10% 4%;
            --border: 240 4% 16%;
            --input: 240 4% 16%;
            --muted: 240 4% 12%;
            --foreground: 0 0% 98%;
            --muted-foreground: 240 5% 65%;
          }
        `}</style>
        <DesktopMarquee prices={displayPrices} />
        <div className="flex gap-2 h-[calc(100vh-2rem)] mt-4">
          <div className="w-[25%]">
            <TokenPriceList 
              prices={displayPrices}
              onSelectToken={(token: any) => {
                setSelectedToken(token.symbol);
                setAmount("");
                setSolAmount("");
                setQuote(null);
              }}
              selectedToken={selectedToken}
            />
          </div>
          
          <div className="w-[50%]">
            <BirdeyeChart 
              tokenAddress={TOKENS.find(t => t.symbol === selectedToken)?.address || TOKENS[0].address} 
              chain="solana"
            />
          </div>
          
          <div className="w-[25%]">
            <SwapCard
              tradeType={tradeType} setTradeType={setTradeType}
              selectedToken={selectedToken} setSelectedToken={setSelectedToken}
              amount={amount} setAmount={setAmount}
              solAmount={solAmount} setSolAmount={setSolAmount}
              quote={quote} quoteLoading={quoteLoading} swapLoading={swapLoading}
              currentPrice={currentPrice}
              getTokenBalance={getTokenBalance}
              handleAmountChange={handleAmountChange} handleSolAmountChange={handleSolAmountChange}
              handleMax={handleMax} handleSwap={() => handleSwap()}
              showTokenDropdown={showTokenDropdown} setShowTokenDropdown={setShowTokenDropdown}
              tokenDropdownRef={tokenDropdownRef} TokenDropdown={TokenDropdown}
              minimumAmount={minimumAmount}
              isDesktop={true}
              navigate={navigate}
            />
          </div>
        </div>
        {notification && <SimpleNotification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
        {passwordDialog.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-xl p-4 w-full max-w-sm space-y-3 shadow-lg">
              <h2 className="text-base font-bold uppercase text-foreground">CONFIRM {tradeType === "buy" ? "BUY" : "SELL"} ORDER</h2>
              <p className="text-[10px] text-muted-foreground uppercase">ENTER PASSWORD TO EXECUTE SWAP</p>
              <div className="space-y-1">
                <label className="text-[10px] font-medium uppercase text-muted-foreground">PASSWORD</label>
                <div className="relative">
                  <Input 
                    type={passwordDialog.showPassword ? "text" : "password"} 
                    placeholder="ENTER PASSWORD" 
                    value={passwordDialog.password} 
                    onChange={(e) => setPasswordDialog({ ...passwordDialog, password: e.target.value })} 
                    disabled={swapLoading} 
                    onKeyPress={(e) => e.key === "Enter" && handleSwap(passwordDialog.password)} 
                    className="uppercase text-xs h-8 bg-card border border-border text-foreground focus:outline-none focus:border-yellow-500" 
                  />
                  <button 
                    onClick={() => setPasswordDialog({ ...passwordDialog, showPassword: !passwordDialog.showPassword })} 
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[8px] uppercase text-muted-foreground"
                  >
                    SHOW
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={() => setPasswordDialog({ open: false, password: "", showPassword: false })} 
                  disabled={swapLoading} 
                  variant="outline" 
                  className="flex-1 text-xs border-border text-foreground hover:bg-muted"
                >
                  CANCEL
                </Button>
                <Button 
                  onClick={() => handleSwap(passwordDialog.password)} 
                  disabled={!passwordDialog.password || swapLoading} 
                  className={cn("flex-1 text-xs", tradeType === "buy" ? "bg-yellow-500 text-white" : "bg-red-500 text-white")}
                >
                  {swapLoading ? "PROCESSING..." : "CONFIRM"}
                </Button>
              </div>
            </div>
          </div>
        )}
        {processingSwap && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card border border-yellow-500/50 rounded-xl p-6 flex items-center gap-3 shadow-lg">
              <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
              <span className="text-sm font-medium uppercase text-yellow-500">Processing swap...</span>
            </div>
          </div>
        )}
        {swapSuccessDialog?.show && (
          <SwapSuccessDialog
            transactionHash={swapSuccessDialog.transactionHash}
            onClose={() => setSwapSuccessDialog({ show: false, transactionHash: '' })}
            tradeType={tradeType}
          />
        )}
        <BackupFileDialog show={showBackupDialog} onClose={handleCancelBackup} onSubmit={handleBackupSubmit} error="" loading={false} />
      </div>
    );
  }

  // Mobile view
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-card py-1">
        <MobileMarquee prices={displayPrices} />
      </div>
      <div className="p-4">
        <SwapCard
          tradeType={tradeType} setTradeType={setTradeType}
          selectedToken={selectedToken} setSelectedToken={setSelectedToken}
          amount={amount} setAmount={setAmount}
          solAmount={solAmount} setSolAmount={setSolAmount}
          quote={quote} quoteLoading={quoteLoading} swapLoading={swapLoading}
          currentPrice={currentPrice}
          getTokenBalance={getTokenBalance}
          handleAmountChange={handleAmountChange} handleSolAmountChange={handleSolAmountChange}
          handleMax={handleMax} handleSwap={() => handleSwap()}
          showTokenDropdown={showTokenDropdown} setShowTokenDropdown={setShowTokenDropdown}
          tokenDropdownRef={tokenDropdownRef} TokenDropdown={TokenDropdown}
          minimumAmount={minimumAmount}
          isDesktop={false}
          navigate={navigate}
        />
      </div>
      {notification && <SimpleNotification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      {passwordDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl p-4 w-full max-w-sm space-y-3 shadow-lg">
            <h2 className="text-base font-bold uppercase text-foreground">CONFIRM {tradeType === "buy" ? "BUY" : "SELL"} ORDER</h2>
            <p className="text-[10px] text-muted-foreground uppercase">ENTER PASSWORD TO EXECUTE SWAP</p>
            <div className="space-y-1">
              <label className="text-[10px] font-medium uppercase text-muted-foreground">PASSWORD</label>
              <div className="relative">
                <Input 
                  type={passwordDialog.showPassword ? "text" : "password"} 
                  placeholder="ENTER PASSWORD" 
                  value={passwordDialog.password} 
                  onChange={(e) => setPasswordDialog({ ...passwordDialog, password: e.target.value })} 
                  disabled={swapLoading} 
                  onKeyPress={(e) => e.key === "Enter" && handleSwap(passwordDialog.password)} 
                  className="uppercase text-xs h-8 bg-card border border-border text-foreground focus:outline-none focus:border-yellow-500" 
                />
                <button 
                  onClick={() => setPasswordDialog({ ...passwordDialog, showPassword: !passwordDialog.showPassword })} 
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[8px] uppercase text-muted-foreground"
                >
                  SHOW
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => setPasswordDialog({ open: false, password: "", showPassword: false })} 
                disabled={swapLoading} 
                variant="outline" 
                className="flex-1 text-xs border-border text-foreground hover:bg-muted"
              >
                CANCEL
              </Button>
              <Button 
                onClick={() => handleSwap(passwordDialog.password)} 
                disabled={!passwordDialog.password || swapLoading} 
                className={cn("flex-1 text-xs", tradeType === "buy" ? "bg-yellow-500 text-white" : "bg-red-500 text-white")}
              >
                {swapLoading ? "PROCESSING..." : "CONFIRM"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {processingSwap && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-yellow-500/50 rounded-xl p-6 flex items-center gap-3 shadow-lg">
            <Loader2 className="w-5 h-5 animate-spin text-yellow-500" />
            <span className="text-sm font-medium uppercase text-yellow-500">Processing swap...</span>
          </div>
        </div>
      )}
      {swapSuccessDialog?.show && (
        <SwapSuccessDialog
          transactionHash={swapSuccessDialog.transactionHash}
          onClose={() => setSwapSuccessDialog({ show: false, transactionHash: '' })}
          tradeType={tradeType}
        />
      )}
      <BackupFileDialog show={showBackupDialog} onClose={handleCancelBackup} onSubmit={handleBackupSubmit} error="" loading={false} />
    </div>
  );
}
